import { Injectable, inject } from '@angular/core';
import { map, of, shareReplay, switchMap } from 'rxjs';

import { AttachmentsService } from './attachments.service';
import { CurrentConfigurationsService } from './current-configuration.service';
import { AttachmentScope } from '@api/backend-connector/models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  private readonly attachmentsService = inject(AttachmentsService);
  private readonly currentConfigurationService = inject(CurrentConfigurationsService);

  private readonly applicationImages$ = this.attachmentsService
    .listStorageObjects({
      scopes: [AttachmentScope.ApplicationLogo, AttachmentScope.ApplicationIcon],
      limit: 2,
    })
    .pipe(
      map(({ items }) => ({
        logo: items.find((a) => a.scope === AttachmentScope.ApplicationLogo),
        icon: items.find((a) => a.scope === AttachmentScope.ApplicationIcon),
      })),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

  private readonly applicationLogoFromSo$ = this.applicationImages$.pipe(
    switchMap(({ logo }) => {
      if (logo) {
        return this.attachmentsService.downloadFile({ id: logo.id }).pipe(map((blob) => URL.createObjectURL(blob)));
      }
      return of(undefined);
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly applicationLogo$ = this.currentConfigurationService.getCurrentConfigurationOrEmpty().pipe(
    switchMap((c) => {
      if (c) {
        if (c.branding?.logo) {
          return of(c.branding.logo);
        }
        return this.applicationLogoFromSo$.pipe(map((img) => img ?? environment.logoPath));
      } else {
        return of(environment.logoPath);
      }
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  private readonly applicationIconFromSo$ = this.applicationImages$.pipe(
    switchMap(({ icon }) => {
      if (icon) {
        return this.attachmentsService.downloadFile({ id: icon.id }).pipe(map((blob) => URL.createObjectURL(blob)));
      }
      return of(undefined);
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly applicationIcon$ = this.currentConfigurationService.getCurrentConfigurationOrEmpty().pipe(
    switchMap((c) => {
      if (c) {
        if (c.branding?.favicon) {
          return of(c.branding.favicon);
        }
        return this.applicationIconFromSo$.pipe(map((img) => img ?? environment.favicon));
      } else {
        return of(environment.favicon);
      }
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly appTitle$ = this.currentConfigurationService.getCurrentConfigurationOrEmpty().pipe(
    map((c) => c?.branding?.appTitle ?? environment.appTitle),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );
}
